import * as THREE from 'https://esm.sh/three@0.160.0';
import { OrbitControls } from 'https://esm.sh/three@0.160.0/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'https://esm.sh/three@0.160.0/examples/jsm/loaders/GLTFLoader.js';


const container = document.getElementsByClassName('parachute-3d-container')[0];
let loadedModel = null;


// Raycasting and selection variables
const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();
let hoveredNode = null;
let selectedNode = null;
let originalMaterials = new Map(); // Store original materials for hover effect
let originalEmissive = new Map(); // Store original emissive values

// Track selected color from swatches
let selectedColorHex = null;
let selectedColorName = null; // Human-readable color name (e.g. "Red") for cart data
let selectedColorMaterial = null;
let coloredNodes = {}; // Store colored nodes for cart submission
let zpEdgeEnabled = true; // Track current ZP edge visibility state
const addonDrivenPanels = new Set(); // Panels colored by add-ons (not manually paintable)
const pendingAddonPanelColors = {}; // Colors to apply once model loads
const addonDrivenPanelColorNames = {}; // Color names for add-on driven panels
const addonDrivenPanelHoverMessages = new Map(); // Custom hover text per add-on driven panel
const FIXED_PANEL_COLORS = {
  TailPocket: '#000000'
};

// Normalize panel names so model and layout stay in sync.
// Rules:
//   - Strip underscore suffixes (e.g. "R1_2" → "R1")
//   - Strip -Full suffix (e.g. "U1-Full" → "U1")
//   - Strip leading zeros from numeric part (e.g. "R01" → "R1")
function normalizePanelName(name) {
  if (!name) return '';
  const raw = String(name).split('_')[0];
  return raw.replace(/-Full$/i, '').replace(/([A-Za-z]+)0+([1-9]\d*)$/, '$1$2');
}

// Track if mouse is down for orbit controls vs. selection
let isMouseDown = false;
let mouseDownTime = 0;
let mouseDownPosition = new THREE.Vector2();

// DOM element for displaying selected node info (optional)
const nodeInfoElement = document.createElement('div');
nodeInfoElement.style.position = 'absolute';
nodeInfoElement.style.bottom = '10px';
nodeInfoElement.style.left = '10px';
nodeInfoElement.style.padding = '5px';
nodeInfoElement.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
nodeInfoElement.style.color = 'white';
nodeInfoElement.style.borderRadius = '4px';
nodeInfoElement.style.fontFamily = 'Arial, sans-serif';
nodeInfoElement.style.fontSize = '14px';
nodeInfoElement.style.pointerEvents = 'none'; // Don't interfere with 3D interaction
nodeInfoElement.style.opacity = '0';
nodeInfoElement.style.transition = 'opacity 0.2s';
container.appendChild(nodeInfoElement);

const scene = new THREE.Scene();
scene.background = new THREE.Color(0xf0f0f0);
window.scene = scene; // Make scene accessible globally

// Load custom background image
const textureLoader = new THREE.TextureLoader();
textureLoader.crossOrigin = 'anonymous';
textureLoader.load(
  'https://cdn.shopify.com/s/files/1/0902/7636/4667/files/canopy-preconfigured-bg.png?v=1749834148',
  (texture) => {
    
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    scene.background = texture;
  },
  (progress) => {
    
  },
  (error) => {
    console.warn('Failed to load custom background, keeping default:', error);
    // Keep the default gray background if loading fails
  }
);

const camera = new THREE.PerspectiveCamera(
  60,
  container.clientWidth / container.clientHeight,
  0.1,
  1000
);
// No initial position - will be set properly after model loads
window.camera = camera; // Make camera accessible globally

// Store original camera position
let originalCameraPosition = null;

const renderer = new THREE.WebGLRenderer({ antialias: true });
renderer.setSize(container.clientWidth, container.clientHeight);
container.appendChild(renderer.domElement);
window.renderer = renderer; // Make renderer accessible globally

// OrbitControls
const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true;
controls.dampingFactor = 0.05;

// Lighting — matte fabric look with clear shape definition
// Moderate ambient: enough to avoid pitch-black shadows, not so much it flattens the shape
const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
scene.add(ambientLight);

// Strong main directional — the primary source of shape/curve definition
const dirLight = new THREE.DirectionalLight(0xffffff, 1.0);
dirLight.position.set(5, 10, 7.5);
scene.add(dirLight);

// Hemisphere light with subtle sky/ground tint for soft environmental wrap
const hemiLight = new THREE.HemisphereLight(0xffffff, 0xd0d8e8, 0.4);
hemiLight.position.set(0, 20, 0);
scene.add(hemiLight);

// Fill light softens the shadow side without flattening the shape
const fillLight = new THREE.DirectionalLight(0xffffff, 0.3);
fillLight.position.set(-5, -8, -5);
scene.add(fillLight);

// Weak back light for subtle edge separation
const backLight = new THREE.DirectionalLight(0xffffff, 0.2);
backLight.position.set(0, 5, -10);
scene.add(backLight);

// Track eligible nodes for the currently selected material
let eligibleNodes = [];

// Load GLB model
function loadModel(url) {
  // Create loading manager first
  const loadingManager = new THREE.LoadingManager();
  loadingManager.onProgress = (url, loaded, total) => {
    
  };
  
  // Create loader with the manager
  const loader = new GLTFLoader(loadingManager);
  
  loader.load(
    url,
    (gltf) => {
      // Success callback
      const model = gltf.scene;
      loadedModel = model; // Store reference to the loaded model
      
      // Clone materials for each mesh to ensure unique materials
      model.traverse((child) => {
        // Make sure every mesh has a unique material that can be modified independently
        if (child instanceof THREE.Mesh && child.material) {
          if (Array.isArray(child.material)) {
            child.material = child.material.map((mat) => mat.clone());
          } else {
            child.material = child.material.clone();
          }
        }
      });
      
      // Center the model exactly at the origin
      const box = new THREE.Box3().setFromObject(model);
      const center = box.getCenter(new THREE.Vector3());
      model.position.sub(center);

      // Fit camera distance so the model fills the viewport comfortably
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const fovRad = (camera.fov * Math.PI) / 180;
      const fitDistance = (maxDim / 2) / Math.tan(fovRad / 2);
      camera.position.set(0, 0, fitDistance * 1.3);

      // Store original camera position
      originalCameraPosition = camera.position.clone();
      window.originalCameraPosition = originalCameraPosition;

      // Orbit controls look at the model center
      controls.target.set(0, 0, 0);
      controls.update();
      
      scene.add(model);
      
      // Store initial materials and emissives for all meshes after model is processed
      model.traverse((child) => {
        if (child instanceof THREE.Mesh && child.material) {
          // Store for originalMaterials map
          if (!originalMaterials.has(child.uuid)) {
            if (Array.isArray(child.material)) {
              originalMaterials.set(child.uuid, child.material.map(m => m.clone()));
            } else {
              originalMaterials.set(child.uuid, child.material.clone());
            }
          }
          // Store for originalEmissive map
          if (!originalEmissive.has(child.uuid)) {
            if (Array.isArray(child.material)) {
              originalEmissive.set(child.uuid,
                child.material.map(m => m.emissive ? m.emissive.clone() : new THREE.Color(0x000000))
              );
            } else if (child.material.emissive) {
              originalEmissive.set(child.uuid, child.material.emissive.clone());
            } else { // Handle cases where material might not have an emissive property
              originalEmissive.set(child.uuid, new THREE.Color(0x000000));
            }
          }
        }
      });
      // Dispatch a modelLoaded event for other components
      const modelLoadedEvent = new CustomEvent('modelLoaded');
      document.dispatchEvent(modelLoadedEvent);
    },
    (xhr) => {
      // Progress callback
      
    },
    (error) => {
      // Error callback
      console.error('Error loading model:', error);
    }
  );
}

// Call with your GLB URL
const modelUrl = window.model3dUrl;
loadModel(modelUrl);

// Render loop
function animate() {
  requestAnimationFrame(animate);
  controls.update();

  renderer.render(scene, camera);
}
animate();

// Resize
window.addEventListener('resize', () => {
  const width = container.clientWidth;
  const height = container.clientHeight;
  camera.aspect = width / height;
  camera.updateProjectionMatrix();
  renderer.setSize(width, height);
});

// Function to update a single node's color with proper vibrancy
function updateNodeColor(node, color) {
  if (!node || !node.material) return;
  
  // Fabric material: matte with visible shape, zero metalness, slight emissive for vibrancy
  if (node.material.isMeshStandardMaterial) {
    node.material.color.set(color);
    node.material.roughness = 0.82;
    node.material.metalness = 0;
    node.material.emissive.set(color);
    node.material.emissiveIntensity = 0.2;
    node.material.needsUpdate = true;
  } else if (Array.isArray(node.material)) {
    node.material.forEach(mat => {
      if (mat.isMeshStandardMaterial) {
        mat.color.set(color);
        mat.roughness = 0.82;
        mat.metalness = 0;
        mat.emissive.set(color);
        mat.emissiveIntensity = 0.2;
        mat.needsUpdate = true;
      }
    });
  }
}

// Update the material properties for brighter, less realistic appearance
function updateNodeColors(colorConfig) {
  if (!loadedModel) {
    console.warn('Model not loaded yet. Colors will not be applied.');
    return;
  }
  

  
  // Traverse the model to find all meshes
  loadedModel.traverse((node) => {
    if (node.isMesh) {
      // Get node name or parent name to match with color config
      const nodeName = node.name;
      const baseName = normalizePanelName(nodeName); // Remove _X part
      
      // Check if this node has a color defined in the config (support normalized aliases)
      const matchedKey = colorConfig[baseName] !== undefined
        ? baseName
        : (colorConfig[nodeName] !== undefined
          ? nodeName
          : Object.keys(colorConfig).find(key => normalizePanelName(key) === baseName));
      
      if (matchedKey) {
        // Handle both direct hex values and object with hex property
        const colorData = colorConfig[matchedKey];
        const color = typeof colorData === 'object' ? colorData.hex : colorData;
        

        
        // Apply consistent vibrant settings using our unified function
        updateNodeColor(node, color);
      }
    }
  });
  
  // Preserve hover effect if a node is being hovered over
  if (hoveredNode) {
    // Apply appropriate hover effect based on painting mode
    if (selectedColorHex) {
      // In painting mode, check eligibility
      const baseName = normalizePanelName(hoveredNode.name);
      const isNodeEligible = canColorNodeWithMaterial(baseName, selectedColorMaterial);
      
      if (isNodeEligible) {
        highlightNode(hoveredNode, 0xffffff, 0.3); // Subtle white for eligible
      } else {
        highlightNode(hoveredNode, 0xff5555, 0.2); // Light red for ineligible
      }
    } else {
      highlightNode(hoveredNode, 0x88ccff, 0.3); // Blue for regular hover
    }
  }
  
  // Force a render update
  renderer.render(scene, camera);
}

// Store original positions of model parts
const originalPositions = new Map();

// Current configuration data
let currentConfiguration = null;
let currentLayout = null;
let allowedMaterialsForCurrentConfig = [];

// Function to update the current configuration rules
function updateConfigurationRules(configData) {
  if (!configData) return;
  
  currentConfiguration = configData.config || null;
  currentLayout = configData.layout || {};
  allowedMaterialsForCurrentConfig = configData.allowedMaterials || [];
}

function isAddonDrivenPanel(nodeName) {
  return addonDrivenPanels.has(normalizePanelName(nodeName));
}

function isFixedColorPanel(nodeName) {
  return Object.prototype.hasOwnProperty.call(FIXED_PANEL_COLORS, normalizePanelName(nodeName));
}

function applyFixedPanelColors() {
  if (!loadedModel) return;

  Object.entries(FIXED_PANEL_COLORS).forEach(([panelName, color]) => {
    loadedModel.traverse((node) => {
      if (node.isMesh && normalizePanelName(node.name) === panelName) {
        updateNodeColor(node, color);
      }
    });
  });
}

function applyAddonPanelColor(panelKey, color, hoverMessage, colorName) {
  const panelName = normalizePanelName(panelKey);
  if (!panelName || !color) return;

  addonDrivenPanels.add(panelName);
  pendingAddonPanelColors[panelName] = color;
  if (colorName) {
    addonDrivenPanelColorNames[panelName] = colorName;
  }
  coloredNodes[panelName] = { name: addonDrivenPanelColorNames[panelName] || panelName, hex: color };
  if (hoverMessage) {
    addonDrivenPanelHoverMessages.set(panelName, hoverMessage);
  }

  if (loadedModel) {
    loadedModel.traverse((node) => {
      if (node.isMesh && normalizePanelName(node.name) === panelName) {
        updateNodeColor(node, color);
      }
    });
  }

  document.dispatchEvent(new CustomEvent('nodeColored', {
    detail: {
      node: panelName,
      color,
      material: null,
      coloredNodes: { ...coloredNodes }
    }
  }));
}

// Function to check if a node can be colored with a specific material
function canColorNodeWithMaterial(nodeName, materialKey) {
  if (isAddonDrivenPanel(nodeName) || isFixedColorPanel(nodeName)) return false;
  if (!currentLayout || !materialKey) return false;
  
  // Check if this node is defined in the layout (allow normalized aliases)
  const normalizedPanelId = normalizePanelName(nodeName);
  let layoutEntry = currentLayout[nodeName] ?? currentLayout[normalizedPanelId];
  
  // Fallback: search for any layout key that normalizes to the same ID
  if (!layoutEntry) {
    const matchedKey = Object.keys(currentLayout).find(
      key => normalizePanelName(key) === normalizedPanelId
    );
    if (matchedKey) {
      layoutEntry = currentLayout[matchedKey];
    }
  }
  
  if (!layoutEntry) return false;
  
  // Get allowed material keys for this panel
  const allowedKeysForPanel = Array.isArray(layoutEntry) 
    ? layoutEntry 
    : [layoutEntry];
  
  // Check if the selected material is allowed for this panel
  return allowedKeysForPanel.includes(materialKey);
}

// Explosion toggle state
let isExploded = false;
let currentExplosionFactor = 0.5; // Default explosion amount
let isAnimating = false; // Track if animation is in progress
let animationFrameId = null; // Store animation frame ID

// Animation settings
const animationDuration = 600; // ms
const animationEasing = t => t<.5 ? 2*t*t : -1+(4-2*t)*t; // Smooth easing function

// Animation state
let animationStartTime = 0;
let startPositions = new Map();
let targetPositions = new Map();

// Function to toggle between exploded and normal view
function toggleExplode(explosionFactor = null) {
  // If already animating, do nothing
  if (isAnimating) return isExploded;
  
  if (explosionFactor !== null) {
    // Update the explosion factor if provided
    currentExplosionFactor = explosionFactor;
  }
  
  // Cancel any existing animation
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }
  // Set up animation
  startAnimatedExplode(!isExploded);
  
  // Return the target state (what we're animating toward)
  return isExploded;
}

// Start animated explosion/collapse
function startAnimatedExplode(explode) {
  if (!loadedModel) return;
  
  // Store the target state
  isExploded = explode;
  
  // Calculate and store start and target positions for each node
  calculateAnimationPositions(explode);
  
  // Start the animation
  isAnimating = true;
  animationStartTime = performance.now();
  animateExplode();
}

// Calculate start and target positions for all nodes
function calculateAnimationPositions(explode) {
  startPositions.clear();
  targetPositions.clear();
  
  // Calculate center of the model
  const modelCenter = new THREE.Vector3();
  const box = new THREE.Box3().setFromObject(loadedModel);
  box.getCenter(modelCenter);
  
  // Calculate the model's overall size to use for consistent movement
  const size = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);
  const baseMovement = maxDim * 0.15 * currentExplosionFactor;
  
  // Store current positions as start positions
  loadedModel.traverse((node) => {
    if (node.isMesh) {
      // Store current position as start position
      startPositions.set(node.uuid, node.position.clone());
      
      // Store original position if not already stored
      if (!originalPositions.has(node.uuid)) {
        originalPositions.set(node.uuid, {
          position: node.position.clone(),
          parent: node.parent
        });
      }
      
      // Calculate target position based on explosion state
      let targetPos;
      
      if (explode) {
        // Calculating exploded position
        const nodeName = node.name;
        const baseName = normalizePanelName(nodeName); // Remove _X part
        
        // Start with current position
        targetPos = node.position.clone();
        
        // Skip middle panels (those starting with R) and Slider
        if (baseName.startsWith('R') || baseName === 'Slider') {
          // Target is same as current - no movement
          targetPositions.set(node.uuid, targetPos);
          return;
        }
        
        // Create a direction vector based on panel type
        let direction = new THREE.Vector3();
        let movementDistance = baseMovement;
        
        if (baseName.startsWith('U')) {
          // Upper panels move up consistently
          direction.set(0, 1, 0);
        } else if (baseName.startsWith('L')) {
          // Lower panels move down consistently
          direction.set(0, -1, 0);
        } else if (baseName.startsWith('S')) {
          // S panels move outward and downward
          const meshCenter = new THREE.Vector3();
          new THREE.Box3().setFromObject(node).getCenter(meshCenter);
          const radial = new THREE.Vector3().subVectors(meshCenter, modelCenter);
          radial.y = 0;
          radial.normalize();
          direction.set(radial.x, -1.0, radial.z).normalize();
        } else {
          // Other panels move outward from center (using classic explosion logic)
          const meshCenter = new THREE.Vector3();
          new THREE.Box3().setFromObject(node).getCenter(meshCenter);
          direction.subVectors(meshCenter, modelCenter).normalize();
          
          // For other panels, we can still scale by distance for a natural explosion
          const distanceFromCenter = meshCenter.distanceTo(modelCenter);
          movementDistance = distanceFromCenter * currentExplosionFactor * 0.5;
        }
        
        // Calculate target position
        targetPos.add(direction.multiplyScalar(movementDistance));
      } else {
        // Collapsing - target is original position
        targetPos = originalPositions.get(node.uuid).position.clone();
      }
      
      // Store target position
      targetPositions.set(node.uuid, targetPos);
    }
  });
}

// Animation frame for explosion/collapse
function animateExplode() {
  const currentTime = performance.now();
  const elapsed = currentTime - animationStartTime;
  const progress = Math.min(elapsed / animationDuration, 1.0);
  const easedProgress = animationEasing(progress);
  
  // Update positions based on progress
  loadedModel.traverse((node) => {
    if (node.isMesh) {
      const uuid = node.uuid;
      if (startPositions.has(uuid) && targetPositions.has(uuid)) {
        const start = startPositions.get(uuid);
        const target = targetPositions.get(uuid);
        
        // Interpolate between start and target
        const newPosition = new THREE.Vector3().lerpVectors(start, target, easedProgress);
        node.position.copy(newPosition);
      }
    }
  });
  
  
  // Render the scene
  renderer.render(scene, camera);
  
  // Continue animation if not complete
  if (progress < 1.0) {
    animationFrameId = requestAnimationFrame(animateExplode);
  } else {
    // Animation complete
    isAnimating = false;
    animationFrameId = null;
    
    // Snap to final positions to ensure precision
    loadedModel.traverse((node) => {
      if (node.isMesh && targetPositions.has(node.uuid)) {
        node.position.copy(targetPositions.get(node.uuid));
      }
    });
    
    // Final render after snapping
    renderer.render(scene, camera);
  }
}


// Function to explode/separate model parts (non-animated version for slider)
function explodeModel(explosionFactor = 1.0) {
  // If animation is in progress, cancel it
  if (isAnimating && animationFrameId) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
    isAnimating = false;
  }
  
  if (!loadedModel) {
    console.warn('Model not loaded yet. Cannot explode.');
    return;
  }
  

  
  // Calculate center of the model
  const modelCenter = new THREE.Vector3();
  const box = new THREE.Box3().setFromObject(loadedModel);
  box.getCenter(modelCenter);
  
  // Calculate the model's overall size to use for consistent movement
  const size = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);
  const baseMovement = maxDim * 0.15 * explosionFactor; // Base movement distance
  
  // Traverse all meshes in the model
  loadedModel.traverse((node) => {
    if (node.isMesh) {
      // Store original position if not already stored
      if (!originalPositions.has(node.uuid)) {
        originalPositions.set(node.uuid, {
          position: node.position.clone(),
          parent: node.parent
        });
      }
      
      // Get the node's base name to determine how it should move
      const nodeName = node.name;
      const baseName = normalizePanelName(nodeName); // Remove _X part
      
      // Reset to original position first (important for re-explosions)
      const origPos = originalPositions.get(node.uuid).position.clone();
      node.position.copy(origPos);
      
      // Skip middle panels (those starting with R) and Slider
      if (baseName.startsWith('R') || baseName === 'Slider') {
        return; // Keep ribs and slider in place
      }
      
      // Create a direction vector based on panel type
      let direction = new THREE.Vector3();
      let movementDistance = baseMovement;
      
      if (baseName.startsWith('U')) {
        // Upper panels move up consistently
        direction.set(0, 1, 0);
      } else if (baseName.startsWith('L')) {
        // Lower panels move down consistently
        direction.set(0, -1, 0);
      } else if (baseName.startsWith('S')) {
        // S panels move outward and downward
        const meshCenter = new THREE.Vector3();
        new THREE.Box3().setFromObject(node).getCenter(meshCenter);
        const radial = new THREE.Vector3().subVectors(meshCenter, modelCenter);
        radial.y = 0;
        radial.normalize();
        direction.set(radial.x, -1.0, radial.z).normalize();
      } else {
        // Other panels move outward from center (using classic explosion logic)
        const meshCenter = new THREE.Vector3();
        new THREE.Box3().setFromObject(node).getCenter(meshCenter);
        direction.subVectors(meshCenter, modelCenter).normalize();
        
        // For other panels, we can still scale by distance for a natural explosion
        const distanceFromCenter = meshCenter.distanceTo(modelCenter);
        movementDistance = distanceFromCenter * explosionFactor * 0.5;
      }
      
      // Apply the movement
      node.position.add(direction.multiplyScalar(movementDistance));
    }
  });
  
  // Update the explosion state
  isExploded = explosionFactor > 0;
  if (explosionFactor > 0) {
    currentExplosionFactor = explosionFactor;
  }
  
  // Force a render update
  renderer.render(scene, camera);
}

// Function to reset the model to its original state
function resetExplode() {
  if (!loadedModel) {
    return;
  }
  
  // If animation is in progress, cancel it
  if (isAnimating && animationFrameId) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
    isAnimating = false;
  }
  
  loadedModel.traverse((node) => {
    if (node.isMesh && originalPositions.has(node.uuid)) {
      // Restore original position
      node.position.copy(originalPositions.get(node.uuid).position);
    }
  });
  
  // Reset explosion state
  isExploded = false;
  
  // Force a render update
  renderer.render(scene, camera);
}

// Function to get the current explosion state
function getExplosionState() {
  return {
    isExploded: isExploded,
    explosionFactor: currentExplosionFactor
  };
}

// Function to get the current colored nodes data
function getColoredNodes() {
  return coloredNodes;
}

// Function to update colored nodes from external source
function setColoredNodes(newColoredNodes) {
  if (newColoredNodes && typeof newColoredNodes === 'object') {
    coloredNodes = {...newColoredNodes};
    updateNodeColors(coloredNodes);
  }
}

// Expose functions to the window object
window.explodeModel = explodeModel;
window.resetExplode = resetExplode;
window.toggleExplode = toggleExplode;
window.getExplosionState = getExplosionState;
window.updateNodeColors = updateNodeColors;
window.getColoredNodes = getColoredNodes;
window.setColoredNodes = setColoredNodes;
window.updateConfigurationRules = updateConfigurationRules;

function isConfirmedDealerCanopy() {
  return Boolean(container.closest("[data-dealer-completed]"));
}

// Add event listeners for mouse interaction
container.addEventListener('mousemove', onMouseMove);
container.addEventListener('click', onClick);
container.addEventListener('mousedown', onMouseDown);
container.addEventListener('mouseup', onMouseUp);

// Listen for color swatch selection from the configurator
document.addEventListener('colorSwatchSelected', function(event) {
  if (isConfirmedDealerCanopy()) return;
  const color = event.detail.color;
  const material = event.detail.material;
  

  selectedColorHex = color;
  selectedColorName = event.detail.name || null;
  selectedColorMaterial = material;
  
  // Update cursor to indicate painting mode
  if (container) {
    container.style.cursor = 'crosshair';
  }
  
  highlightEligibleNodes();
});

// Listen for add-on driven panel colors (e.g. Trim Tape → Vents)
document.addEventListener('addonPanelColorSelected', function(event) {
  if (isConfirmedDealerCanopy()) return;
  const panel = event.detail?.panel;
  const color = event.detail?.color;
  const hoverMessage = event.detail?.hoverMessage;
  const colorName = event.detail?.name;
  applyAddonPanelColor(panel, color, hoverMessage, colorName);
});

document.addEventListener('modelLoaded', function() {
  Object.entries(pendingAddonPanelColors).forEach(([panel, color]) => {
    applyAddonPanelColor(panel, color);
  });
  applyFixedPanelColors();
});

// Listen for slider color selection and apply to slider meshes
document.addEventListener('sliderColorSelected', function(event) {
  const color = event.detail.color;
  if (!color || !loadedModel) return;
  
  const sliderKey = 'Slider';
  // Persist selection for re-renders/cart sync
  coloredNodes[sliderKey] = { name: event.detail.name || sliderKey, hex: color };
  
  // Apply to all meshes named Slider (normalized)
  loadedModel.traverse((node) => {
    if (node.isMesh && normalizePanelName(node.name) === sliderKey) {
      updateNodeColor(node, color);
    }
  });
  
  // Notify other listeners (e.g., cart sync) that a node was colored
  const colorEvent = new CustomEvent('nodeColored', {
    detail: {
      node: sliderKey,
      color,
      material: null,
      coloredNodes: coloredNodes
    }
  });
  document.dispatchEvent(colorEvent);
});

// Listen for ZP Edge toggle to swap front-face variants
document.addEventListener('zpEdgeToggled', function(event) {
  const enabled = !!event.detail?.enabled; // true => show ZPEdge, hide -Full
  zpEdgeEnabled = enabled;
  if (!loadedModel) return;
  
  loadedModel.traverse((node) => {
    // Apply to both meshes and groups to catch grouped panels
    const isZPEdge = node.userData?.zpVariant === true || normalizePanelName(node.name) === 'ZPEdge';
    const isFullFront = node.userData?.fullFrontVariant === true || /-Full$/i.test(node.name);
    
    if (isZPEdge) {
      node.visible = enabled;
    } else if (isFullFront) {
      node.visible = !enabled;
    }
  });
  
  // Clear outlines to avoid highlighting hidden parts
  if (typeof clearNodeOutlines === 'function') {
    clearNodeOutlines();
  }
  
  // If we're in painting mode, re-highlight eligible visible nodes
  if (selectedColorMaterial && typeof highlightEligibleNodes === 'function') {
    highlightEligibleNodes();
  }
});

// Mouse movement handler for hover effect
function onMouseMove(event) {
  // Calculate mouse position in normalized device coordinates
  const rect = renderer.domElement.getBoundingClientRect();
  mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  
  // Only do hover detection if we're not currently dragging
  if (!isMouseDown) {
    checkIntersection();
  }
}

// Check for intersections between mouse and objects
function checkIntersection() {
  if (isConfirmedDealerCanopy()) return;
  if (!loadedModel) return;
  
  // Check if this is a preconfigured/preset product - if so, skip hover effects
  const threeDContainer = document.querySelector('.parachute-3d-container[data-preset="true"]');
  if (threeDContainer) {
    // This is a preconfigured product - disable hover interactions
    if (container) {
      container.style.cursor = 'default';
    }
    nodeInfoElement.style.opacity = '0';
    return;
  }
  
  // Update the picking ray with the camera and mouse position
  raycaster.setFromCamera(mouse, camera);
  
  // Get all intersected objects
  const intersects = raycaster.intersectObject(loadedModel, true);
  
  // Pick the first visible, allowed intersection (skip hidden ZP when disabled)
  const validIntersect = intersects.find(hit => {
    const obj = hit.object;
    if (!obj.visible) return false;
    const baseName = normalizePanelName(obj.name);
    if (!zpEdgeEnabled && baseName === 'ZPEdge') return false;
    return true;
  });
  
  // Clear previous hover state
  if (hoveredNode) {
    resetNodeMaterial(hoveredNode);
  }
  
  hoveredNode = null;
  
  // Set cursor style based on painting mode
  container.style.cursor = selectedColorHex ? 'crosshair' : 'default';
  
  if (validIntersect) {
    const intersectedObject = validIntersect.object;
    
    // Apply hover effect
    hoveredNode = intersectedObject;
    const nodeName = hoveredNode.name;
    const baseName = normalizePanelName(nodeName); // Remove _X part
    
    // Check if this node can accept the current material (if in painting mode)
    const isNodeEligible = selectedColorHex && canColorNodeWithMaterial(baseName, selectedColorMaterial);
    
    // Apply appropriate hover effect
    if (selectedColorHex) {
      // In painting mode, use different hover effects for eligible vs ineligible nodes
      if (isNodeEligible) {
        // Use a vibrant hover effect for eligible nodes
        highlightNode(hoveredNode, 0xffffff, 0.3); // Subtle white for eligible
        container.style.cursor = 'crosshair';
      } else {
        // Use a red-tinted hover for ineligible nodes
        highlightNode(hoveredNode, 0xff5555, 0.2); // Light red for ineligible
        container.style.cursor = 'not-allowed';
      }
    } else {
      // Standard hover in non-painting mode
      highlightNode(hoveredNode, 0x88ccff, 0.3); // Light blue
      container.style.cursor = 'pointer';
    }
    
    // Update info element with node name
    let infoText;
    if (isAddonDrivenPanel(baseName)) {
      infoText = addonDrivenPanelHoverMessages.get(baseName)
        || 'This panel is colored by an add-on selection.';
      if (selectedColorHex) {
        highlightNode(hoveredNode, 0xff5555, 0.2);
        container.style.cursor = 'not-allowed';
      }
    } else if (selectedColorHex) {
      if (isNodeEligible) {
        infoText = `Click to apply this color`;
      } else {
        const allowedLabels = getAllowedMaterialLabels(baseName);
        infoText = allowedLabels.length
          ? `This panel uses colors from ${allowedLabels.join(', ')}`
          : `Cannot apply this color to this panel`;
      }
    } else {
      const labels = getAllowedMaterialLabels(baseName);
      infoText = labels.length
        ? `Select a color from ${labels.join(' or ')} for this panel`
        : `${baseName}`;
    }
    nodeInfoElement.textContent = infoText;
    nodeInfoElement.style.opacity = '1';
  } else {
    nodeInfoElement.style.opacity = '0';
  }
}

// Return human-readable labels for every material group a panel belongs to.
// Reads group titles from the already-rendered color picker DOM so the labels
// are always consistent with what the user sees in the sidebar.
function getAllowedMaterialLabels(baseName) {
  if (!currentLayout) return [];
  const normalizedId = normalizePanelName(baseName);
  const entry = currentLayout[baseName]
    ?? currentLayout[normalizedId]
    ?? (() => {
      const key = Object.keys(currentLayout).find(k => normalizePanelName(k) === normalizedId);
      return key ? currentLayout[key] : null;
    })();
  if (!entry) return [];
  const keys = Array.isArray(entry) ? entry : [entry];

  // Deduplicate by the first segment of the key (e.g. "zp" covers "zp-skydive" & "zp-base")
  const seen = new Set();
  return keys.reduce((labels, key) => {
    const group = key.split('-')[0];
    if (seen.has(group)) return labels;
    seen.add(group);
    // Prefer the label from the DOM (matches what the user reads in the color picker)
    const swatchEl = document.querySelector(`[data-material-key="${key}"]`);
    const domLabel = swatchEl
      ?.closest('.material-group')
      ?.querySelector('.material-group-title')
      ?.dataset.baseTitle
      ?.replace(':', '')
      ?.trim();
    if (domLabel) { labels.push(domLabel); return labels; }
    // Fallback: format the key segment
    if (group === 'zp') { labels.push('ZP'); return labels; }
    if (group === 'f111') { labels.push('F111'); return labels; }
    labels.push(group.charAt(0).toUpperCase() + group.slice(1));
    return labels;
  }, []);
}

// Highlight a node by changing its material properties
function highlightNode(node, color, intensity) {
  if (!node || !node.material) return;
  
  // Store original material if not already stored
  if (!originalMaterials.has(node.uuid)) {
    if (Array.isArray(node.material)) {
      originalMaterials.set(node.uuid, node.material.map(m => m.clone()));
    } else {
      originalMaterials.set(node.uuid, node.material.clone());
    }
  }
  
  // Store original emissive color
  if (!originalEmissive.has(node.uuid)) {
    if (Array.isArray(node.material)) {
      originalEmissive.set(node.uuid, 
        node.material.map(m => m.emissive ? m.emissive.clone() : new THREE.Color(0x000000)));
    } else if (node.material.emissive) {
      originalEmissive.set(node.uuid, node.material.emissive.clone());
    }
  }
  
  // Apply highlight effect
  if (Array.isArray(node.material)) {
    node.material.forEach(mat => {
      if (mat.emissive) {
        mat.emissive.set(color);
        mat.emissiveIntensity = intensity;
      }
    });
  } else if (node.material.emissive) {
    node.material.emissive.set(color);
    node.material.emissiveIntensity = intensity;
  }
  
  node.material.needsUpdate = true;
}

// Reset node material to its original state
function resetNodeMaterial(node) {
  if (!node || !originalMaterials.has(node.uuid)) return;
  
  if (Array.isArray(node.material)) {
    const origMaterials = originalEmissive.get(node.uuid);
    node.material.forEach((mat, index) => {
      if (mat.emissive && origMaterials && origMaterials[index]) {
        mat.emissive.copy(origMaterials[index]);
        mat.emissiveIntensity = 1.0;
      }
    });
  } else if (node.material.emissive && originalEmissive.has(node.uuid)) {
    node.material.emissive.copy(originalEmissive.get(node.uuid));
    node.material.emissiveIntensity = 1.0;
  }
  
  node.material.needsUpdate = true;
  
  // Force update after hover reset to ensure colors are properly vibrant
  const baseName = normalizePanelName(node.name);
  if (coloredNodes[baseName]) {
    // Re-apply the color with full vibrancy
    const colorConfig = {};
    colorConfig[baseName] = coloredNodes[baseName];
    updateNodeColor(node, colorConfig[baseName].hex);
  }
}

// Mouse down handler
function onMouseDown(event) {
  isMouseDown = true;
  mouseDownTime = Date.now();
  mouseDownPosition.set(event.clientX, event.clientY);
  
  // Set cursor to grabbing during potential drag operation
  if (container) {
    container.style.cursor = 'grabbing';
  }
}

// Mouse up handler
function onMouseUp(event) {
  isMouseDown = false;
  
  // Restore appropriate cursor
  if (container) {
    if (selectedColorHex) {
      container.style.cursor = 'crosshair'; // Painting mode
    } else {
      container.style.cursor = 'default';
    }
  }
  
  // Reset checkIntersection to update hover state
  checkIntersection();
}

// Click handler for selection
function onClick(event) {
  if (isConfirmedDealerCanopy()) return;
  // Check if this is a preconfigured/preset product - if so, skip click interactions
  const threeDContainer = document.querySelector('.parachute-3d-container[data-preset="true"]');
  if (threeDContainer) {
    // This is a preconfigured product - disable click interactions
    return;
  }
  
  // Only consider it a click if it was a short press (not a drag) and didn't move much
  const clickDuration = Date.now() - mouseDownTime;
  const moveDistance = Math.sqrt(
    Math.pow(event.clientX - mouseDownPosition.x, 2) + 
    Math.pow(event.clientY - mouseDownPosition.y, 2)
  );
  
  if (clickDuration > 300 || moveDistance > 10) {
    // This was probably a drag, not a click
    return;
  }
  
  if (!loadedModel) return;
  
  // Update the picking ray with the camera and mouse position
  raycaster.setFromCamera(mouse, camera);
  
  // Get all intersected objects
  const intersects = raycaster.intersectObject(loadedModel, true);
  
  // Pick the first visible, allowed intersection (skip hidden ZP when disabled)
  const validIntersect = intersects.find(hit => {
    const obj = hit.object;
    if (!obj.visible) return false;
    const baseName = normalizePanelName(obj.name);
    if (!zpEdgeEnabled && baseName === 'ZPEdge') return false;
    return true;
  });

  if (validIntersect) {
    const intersectedObject = validIntersect.object;
    const nodeName = intersectedObject.name;
    const baseName = normalizePanelName(nodeName); // Remove _X part

    // Clicks only apply colors in painting mode; hover tooltips handle everything else.
    if (!selectedColorHex) return;

    // Check if this node can accept the selected material
    const canApplyColor = canColorNodeWithMaterial(baseName, selectedColorMaterial);

    if (canApplyColor) {
        // Store the colored node information (name = color name for cart data)
        coloredNodes[baseName] = {
          name: selectedColorName || baseName,
          hex: selectedColorHex
        };
        
        // Apply the color with proper vibrancy directly to the intersected object
        updateNodeColor(intersectedObject, selectedColorHex);
        
        // Find and update all meshes with the same baseName for consistent coloring
        loadedModel.traverse((node) => {
          if (node.isMesh && normalizePanelName(node.name) === baseName && node !== intersectedObject) {
            updateNodeColor(node, selectedColorHex);
          }
        });
        
        // Trigger a custom event to notify of the coloring
        const colorEvent = new CustomEvent('nodeColored', {
          detail: {
            node: baseName,
            color: selectedColorHex,
            material: selectedColorMaterial,
            coloredNodes: coloredNodes
          }
        });
        document.dispatchEvent(colorEvent);
        
        // Update the info element to show which node was colored
        // nodeInfoElement.textContent = `Colored: ${baseName}`;
        // nodeInfoElement.style.opacity = '1';
        
        // Hide the notification after 2 seconds
        setTimeout(() => {
          if (nodeInfoElement.textContent.includes('Colored:')) {
            nodeInfoElement.style.opacity = '0';
          }
        }, 2000);
        
      // Keep the outline visible — the panel remains eligible for recoloring
    } else {
      // Show a notification that this material can't be applied to this node
      nodeInfoElement.textContent = `Cannot apply this material to this panel`;
      nodeInfoElement.style.opacity = '1';

      // Hide the notification after 2 seconds
      setTimeout(() => {
        if (nodeInfoElement.textContent.includes('Cannot apply')) {
          nodeInfoElement.style.opacity = '0';
        }
      }, 2000);
    }
  }
}

// Expose getSelectedNode function to the window
window.getSelectedNode = function() {
  if (selectedNode) {
    const nodeName = selectedNode.name;
    const baseName = normalizePanelName(nodeName);
    return { node: selectedNode, name: baseName };
  }
  return null;
};

// Function to color all eligible nodes with the currently selected color
function colorAllEligibleNodes() {
  if (isConfirmedDealerCanopy()) return;
  if (!selectedColorHex || !selectedColorMaterial || !currentLayout || !loadedModel) {
    console.warn('Cannot color all nodes: missing required data');
    return;
  }
  

  
  // Use the eligible nodes list if available, otherwise use the layout
  if (eligibleNodes.length > 0) {
    // Extract base names from eligible nodes
    const eligibleNodeBaseNames = eligibleNodes.map(node => {
      return normalizePanelName(node.name);
    });
    
    // Remove duplicates (multiple mesh parts with same base name)
    const uniqueEligibleBaseNames = [...new Set(eligibleNodeBaseNames)];
    

    
    // Store color for each eligible node and apply directly with proper vibrancy
    uniqueEligibleBaseNames.forEach(baseName => {
      if (isAddonDrivenPanel(baseName) || isFixedColorPanel(baseName)) return;

      coloredNodes[baseName] = {
        name: selectedColorName || baseName,
        hex: selectedColorHex
      };
      
      // Find and update all nodes with this base name
      loadedModel.traverse((node) => {
        if (node.isMesh && normalizePanelName(node.name) === baseName) {
          updateNodeColor(node, selectedColorHex);
        }
      });
    });
    
    // Rebuild outlines after coloring so they remain visible
    highlightEligibleNodes();
  } else {
    // Fallback to using the layout configuration
    Object.keys(currentLayout).forEach(panelId => {
      // Check if this panel can accept the selected material
      const canApplyColor = canColorNodeWithMaterial(panelId, selectedColorMaterial);
      
      if (canApplyColor) {
        const normalizedPanelId = normalizePanelName(panelId);
        // Store the colored node information (name = color name for cart data)
        coloredNodes[normalizedPanelId] = {
          name: selectedColorName || normalizedPanelId,
          hex: selectedColorHex
        };
        
        // Find and update all nodes with this panel ID
        loadedModel.traverse((node) => {
          if (node.isMesh && normalizePanelName(node.name) === normalizedPanelId) {
            updateNodeColor(node, selectedColorHex);
          }
        });
      }
    });
  }
  
  // Trigger a custom event to notify of the bulk coloring
  const colorEvent = new CustomEvent('nodeColored', {
    detail: {
      node: 'all',
      color: selectedColorHex,
      material: selectedColorMaterial,
      coloredNodes: coloredNodes
    }
  });
  document.dispatchEvent(colorEvent);
}

// Highlight all nodes that can accept the selected material
function highlightEligibleNodes() {
  // Clear previous highlights
  clearNodeOutlines();
  
  if (!selectedColorMaterial || !currentLayout || !loadedModel) return;
  

  
  // Find all nodes that can accept this material
  loadedModel.traverse((node) => {
    if (node.isMesh) {
      const nodeName = node.name;
      const baseName = normalizePanelName(nodeName); // Remove _X part
      
      // Skip nodes that are currently hidden (e.g., ZP toggle)
      if (!node.visible) return;
      
      // Check if this node can accept the selected material
      const canApplyColor = canColorNodeWithMaterial(baseName, selectedColorMaterial);
      
      if (canApplyColor) {
        eligibleNodes.push(node);
      }
    }
  });
}

// Clear eligible node tracking (no visual effect — just bookkeeping)
function clearNodeOutlines() {
  eligibleNodes = [];
}

// Updated reset painting mode function
function resetPaintingMode() {
  selectedColorHex = null;
  selectedColorMaterial = null;
  
  // Clear outlines
  clearNodeOutlines();
  
  if (container) {
    container.style.cursor = 'default';
  }
  
  // Disable the Color All button
  if (window.document) {
    const colorAllButtons = window.document.querySelectorAll('[id^="color-all-button-"]');
    colorAllButtons.forEach(button => {
      button.disabled = true;
    });
  }
  
  // Update hover effect
  if (hoveredNode) {
    highlightNode(hoveredNode, 0x88ccff, 0.3); // Blue for regular hover
  }
  
  
}

// Expose functions to the window object
window.normalizePanelName = normalizePanelName;
window.resetPaintingMode = resetPaintingMode;
window.colorAllEligibleNodes = colorAllEligibleNodes;
window.getColoredNodes = getColoredNodes;
window.setColoredNodes = setColoredNodes;
window.updateConfigurationRules = updateConfigurationRules;

// Remove a specific panel (by base name) from the eligible set
function removeNodeOutlines(baseName) {
  if (!baseName) return;
  eligibleNodes = eligibleNodes.filter(node => normalizePanelName(node.name) !== baseName);
}

// Function to reset all custom colors and model appearance
function resetAllCustomColors() {
  if (!loadedModel) return;

  loadedModel.traverse((node) => {
    if (node.isMesh) {
      // Revert to original material if it was stored
      if (originalMaterials.has(node.uuid)) {
        const originalMaterialOrMaterials = originalMaterials.get(node.uuid);
        if (Array.isArray(originalMaterialOrMaterials)) {
          node.material = originalMaterialOrMaterials.map(m => m.clone());
        } else {
          node.material = originalMaterialOrMaterials.clone();
        }
        node.material.needsUpdate = true;
      }
      // If no original material stored (e.g., node was never hovered/highlighted),
      // ensure its current material is just a basic one if it was colored.
      // This part is tricky without knowing the *true* default if never interacted.
      // For now, relying on originalMaterials covers interacted parts.
    }
  });

  // Clear the record of colored nodes, then restore add-on driven panel colors
  coloredNodes = {};
  Object.entries(pendingAddonPanelColors).forEach(([panel, color]) => {
    applyAddonPanelColor(panel, color);
  });
  applyFixedPanelColors();

  // Clear stored original emissive values as materials are fully reset
  originalEmissive.clear();

  // It might be good to also clear originalMaterials if we want a truly fresh slate
  // in case the model itself could change, but for color reset, this is likely sufficient.
  // originalMaterials.clear();

  
  renderer.render(scene, camera);
}
window.resetAllCustomColors = resetAllCustomColors;